import type { FC } from 'react';
import React, { memo, useState } from 'react';
import { CheckCircle, Clipboard, Download } from '@phosphor-icons/react';
import { Prism } from 'react-syntax-highlighter';
import type { SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';

import { Button } from '../ui/button';
import { generateRandomString, programmingLanguages } from './utils/codeblock';

interface Props {
  language: string;
  value: string;
}

const HighlighterComponent = Prism as unknown as React.ComponentType<SyntaxHighlighterProps>;

export const CodeBlock: FC<Props> = memo(({ language, value }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const downloadAsFile = () => {
    const fileExtension = programmingLanguages[language] || '.file';
    const suggestedFileName = `file-${generateRandomString(3, true)}${fileExtension}`;
    const fileName = suggestedFileName;

    const blob = new Blob([value], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = fileName;
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative font-sans text-sm mt-1">
      <div className="relative rounded-t-lg bg-zinc-800/80 font-sans text-sm backdrop-blur">
        <div className="flex h-8 items-center justify-between px-3">
          <span className="text-xs font-medium text-zinc-400">{language}</span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-5 text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-100"
              onClick={() => void copyToClipboard()}
            >
              {isCopied ? <CheckCircle size={18} /> : <Clipboard size={18} />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-5 text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-100"
              onClick={downloadAsFile}
            >
              <Download size={18} />
            </Button>
          </div>
        </div>
      </div>
      <div className="rounded-b-lg">
        <HighlighterComponent
          language={language}
          style={a11yDark}
          PreTag="div"
          wrapLongLines
          customStyle={{
            margin: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {value}
        </HighlighterComponent>
      </div>
    </div>
  );
});

CodeBlock.displayName = 'CodeBlock';
